/* eslint-disable tailwindcss/no-custom-classname */
'use client';

import classNames from 'classnames';
import { CompareToggle } from '../../cms-blocks/Compare/CompareToggle';
import { CompareTogglePill } from '../../cms-blocks/Compare/CompareTogglePill';
import { RESULTS_PAGE_ITEM_LIST_NAME } from '../../cms-blocks/Compare/tracking';
import { ApplyCtaLink } from '../../components/ApplyCta';
import { FeLink } from '../../components/FeLink';
import { CarInsuranceProductComponentButtonTypeEnum } from '../../types/cmsEnums';
import {
  ComponentTypeEnum,
  configureCiFilterApplied,
  configureCiSearchResultParameters,
  configureTiSearchResultParameters,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics';
import { getApprovalOddsStatus } from '../../utils/approvalOdds';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatNumberWithSeparator } from '../../utils/helpers';
import { sessionAttributionTracker } from '../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { useCurrentCountry } from '../../utils/translations/useCurrentCountry';
import Button, { ButtonStyleType } from '../Button';
import {
  CarInsuranceCtaBuilder,
  CarInsuranceCtaBuilderInstance,
} from '../CarInsurance/CarInsuranceCtaBuilder';
import { useSafeCarInsuranceContext } from '../CarInsurance/context/useSafeCarInsurance';
import { EligibilityNote } from '../Eligibility/EligibilityNote';
import { MaterialIcon } from '../MaterialIcon';
import SectionButton from '../SectionButton';
import { Tag } from '../Tag';
import {
  TravelInsuranceCtaBuilder,
  TravelInsuranceCtaType,
} from '../TravelInsuranceCtaBuilder';
import { TRANSLATIONS } from './i18n';
import { ProductCardHeader } from './ProductCardHeader';
import { ProductCardSectionsMapper } from './ProductCardSectionsMapper';
import { SectionApprovalOdds } from './SectionApprovalOdds';
import { SectionAttributes } from './SectionAttributes';
import { SectionCarInsurance } from './SectionCarInsurance';
import { SectionTravelInsurance } from './SectionTravelInsurance';
import { ProductCardProps } from './types';
import { getCiPriceValue, getTiPriceValue } from './utils';

export const ProductCard: React.FC<ProductCardProps> = (props) => {
  const {
    vertical,
    pageType,
    componentAttributes,
    textTag,
    colorTag,
    iconTag,
    ctaLinkOverride,
    ctaLabelOverride,
    view_rating,
    url,
    index,
    loanSearchParams: loanParams,
    productWithLivePrice,
    carInsuranceSection,
    calculateError = false,
    carInsuranceQuote,
    highlightFirstAttribute,
    travelInsuranceQuotes,
    travelQuotes,
    urlPlanDetails,
    e2eLabel,
    e2eToogleButton,
    statusCarInsuranceRequest,
    product,
    defaultTemplateDynamicCiTooltip,
    defaultStaticCiTooltip,
    pushDataLayer,
    plFilterConfig,
    approvalOdds,
    approvalOddsState,
    approvalOddsLabels,
    onApprovalOddsUnlockClick,
    onApprovalOddsRefreshClick,
    productDetailsModalKeys,
    getPdpModalConfigAction,
    isPreview,
    renderPdpModalHero,
    eligibility,
    deEmphasised,
  } = props;

  const {
    uid,
    title,
    product_name,
    call_to_action,
    attributes: productAttributes,
  } = product;

  const context = useCmsClientContext();
  const countryCode = useCurrentCountry();
  const carInsuranceContext = useSafeCarInsuranceContext();

  const staticText = getClientTranslations(TRANSLATIONS, context);

  const tiTrackingData = () => {
    if (vertical !== 'TRAVEL_INSURANCE') return {};

    return {
      search_result_parameter: configureTiSearchResultParameters(travelQuotes),
      item_list_name: RESULTS_PAGE_ITEM_LIST_NAME,
      filter_applied: {},
    };
  };

  const tiPriceValue = getTiPriceValue(travelInsuranceQuotes);

  const tiTrackingItemData = () => {
    if (vertical !== 'TRAVEL_INSURANCE') return {};

    return {
      price: tiPriceValue,
      currency: getCurrency(),
    };
  };

  const ciTrackingData = () => {
    if (vertical === 'CAR_INSURANCE') {
      return {
        filter_applied: configureCiFilterApplied(carInsuranceContext),
        search_result_parameter:
          configureCiSearchResultParameters(carInsuranceContext),
        item_list_name: 'ss-ci-rp',
      };
    } else return {};
  };

  const ciPriceValue = getCiPriceValue(
    productWithLivePrice,
    carInsuranceQuote,
    carInsuranceSection,
  );

  const getCurrency = () => {
    return context.brand === 'singsaver'
      ? 'SGD'
      : context.brand === 'money101'
        ? 'NT'
        : context.currencyDisplay;
  };

  const ciTrackingItemData = () => {
    if (vertical === 'CAR_INSURANCE') {
      return {
        discount: '',
        price: ciPriceValue,
        currency: getCurrency(),
      };
    } else return {};
  };

  const getAnalyticsExtraData = () => {
    if (vertical === 'CAR_INSURANCE') {
      return {
        extraData: ciTrackingData(),
        itemExtraData: ciTrackingItemData(),
      };
    }

    if (vertical === 'TRAVEL_INSURANCE') {
      return {
        extraData: tiTrackingData(),
        itemExtraData: tiTrackingItemData(),
      };
    } else
      return {
        extraData: {},
        itemExtraData: {},
      };
  };

  const sendAnalyticsForButtonApply = () => {
    sessionAttributionTracker.recordApplyClick();
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Product Card/CTA_Apply Now',
      componentType: ComponentTypeEnum.PRODUCT_CARD,
      event: 'product_click_apply',
      index: index,
      url: url,
      product,
      extraData: getAnalyticsExtraData().extraData,
      itemExtraData: getAnalyticsExtraData().itemExtraData,
      pushDataLayer,
      approvalDegree: approvalOdds,
      approvalDegreeState: approvalOddsState,
    });
  };

  const sendAnalyticsForApprovalOddsUnlock = () => {
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Product Card/Approval Odds Strip_Unlock',
      componentType: ComponentTypeEnum.PRODUCT_CARD,
      event: 'product_interaction',
      index: index,
      url: url,
      product,
      extraData: getAnalyticsExtraData().extraData,
      itemExtraData: getAnalyticsExtraData().itemExtraData,
      pushDataLayer,
      approvalDegree: approvalOdds,
      approvalDegreeState: approvalOddsState,
    });
    onApprovalOddsUnlockClick?.();
  };

  const sendAnalyticsForonApprovalOddsRefresh = () => {
    const { isFresh } = getApprovalOddsStatus(approvalOddsState);

    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: isFresh
        ? 'Product Card/Approval Odds Strip_Label Details'
        : 'Product Card/Approval Odds Strip_Refresh Expired',
      componentType: ComponentTypeEnum.PRODUCT_CARD,
      event: 'product_interaction',
      index: index,
      url: url,
      product,
      extraData: getAnalyticsExtraData().extraData,
      itemExtraData: getAnalyticsExtraData().itemExtraData,
      pushDataLayer,
      approvalDegree: approvalOdds,
      approvalDegreeState: approvalOddsState,
    });
    onApprovalOddsRefreshClick?.();
  };

  const policyAttribute = productAttributes
    ?.filter((a) => a.__typename === 'ComponentAttributesAttrPlainText')
    .find((a) => a.name?.name === 'POLICY_WORDING_DOCUMENT_LINK')?.value?.[0];

  const ctaLink = call_to_action?.links?.[0];

  const displayDefaultCtaButton =
    (ctaLink?.redirected_url || ctaLinkOverride) &&
    (!['CAR_INSURANCE', 'TRAVEL_INSURANCE'].includes(vertical ?? '') ||
      pageType === 'article');

  let carInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null = null;
  let priceLine: React.ReactNode | null = null;
  if (vertical === 'CAR_INSURANCE') {
    const showPriceCheck =
      context.brand === 'singsaver'
        ? statusCarInsuranceRequest === 'fail' ||
          statusCarInsuranceRequest === 'Incomplete' ||
          !productWithLivePrice
        : !carInsuranceQuote?.totalPremium;
    const buttonStyleMap: Record<
      CarInsuranceProductComponentButtonTypeEnum,
      ButtonStyleType
    > = {
      [CarInsuranceProductComponentButtonTypeEnum.Cta]: 'cta',
      [CarInsuranceProductComponentButtonTypeEnum.Neutral]:
        'neutral-light-and-blue',
      [CarInsuranceProductComponentButtonTypeEnum.NeutralLightAndBlue]:
        'neutral-light-and-blue',
      [CarInsuranceProductComponentButtonTypeEnum.Primary]: 'primary',
      [CarInsuranceProductComponentButtonTypeEnum.Secondary]: 'secondary',
    };

    carInsuranceCtaComponent = CarInsuranceCtaBuilder({
      showPriceCheck,
      carInsuranceSection,
      buttonStyleMap,
      ctaLinkOverride,
      ctaLabelOverride,
    });

    const viewPrice = formatNumberWithSeparator({
      value: ciPriceValue,
      thousandsSeparator: ',',
      alwaysTwoDecimals: true,
    });
    priceLine =
      viewPrice === '0' || viewPrice === '0.00' ? null : (
        <div>
          <div
            className={classNames(
              'order-1 flex flex-row items-center text-right md:order-2 gap-spacing/gap/soft-6px md:text-base text-font-size/xs line-clamp-1 break-all',
            )}
          >
            <div className="inline-flex text-xs md:text-base justify-end items-start gap-spacing/gap/soft-6px">
              <div className="text-text/secondary-lighter font-medium leading-5 whitespace-nowrap">
                {staticText.starting_at}
              </div>
              <div className="flex items-center gap-0.5">
                {carInsuranceSection?.prefix && (
                  <div className="text-text/secondary-lighter font-medium leading-5 whitespace-nowrap">
                    <sup>{carInsuranceSection?.prefix}</sup>
                  </div>
                )}
                <div className="text-text/primary font-bold leading-5 line-clamp-1">
                  {viewPrice}
                </div>
                {carInsuranceSection?.suffix && (
                  <div className="text-text/secondary-lighter font-medium leading-5 line-clamp-1">
                    <sup>{carInsuranceSection?.suffix}</sup>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
  }

  const travelInsuranceCtaComponent = (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => {
    return vertical !== 'TRAVEL_INSURANCE' ? undefined : (
      <TravelInsuranceCtaBuilder
        product={product}
        ctaLinkOverride={ctaLinkOverride}
        sendAnalyticsForButtonApply={sendAnalyticsForButtonApply}
        ctaLabelOverride={ctaLabelOverride}
        e2eToogleButton={e2eToogleButton}
        e2eLabel={e2eLabel}
        travelInsuranceCtaType={travelInsuranceCtaType}
      />
    );
  };

  return (
    <div
      className={classNames(
        'card-radius border border-borders/default p-3',
        // Tinted rather than faded: fading the card would take the Apply button
        // with it, and requirement 4b keeps that at full prominence.
        deEmphasised ? 'bg-fill/alternative-1' : 'bg-fill/card',
      )}
      id={`anchor-card-${uid}`}
      data-product-uid={title}
    >
      <div
        className="mb-3 flex items-start justify-between gap-2"
        id={`anchor-${product_name}`}
      >
        {!!textTag?.trim() && (
          <Tag size="large" color={colorTag?.toLowerCase()} icon={iconTag}>
            <h2>{`${textTag}`}</h2>
          </Tag>
        )}
        {/* Top right, clear of the badge. Renders only where the vertical compares with the pill
            (credit cards) on a results page that offers comparison. */}
        <CompareTogglePill product={product} className="ml-auto shrink-0" />
      </div>
      <div className="flex flex-col items-start justify-center gap-4 md:flex-row md:items-center md:justify-between md:gap-2">
        <ProductCardHeader
          vertical={vertical!}
          product={product}
          index={index}
          url={url}
          view_rating={view_rating}
          componentAttributes={componentAttributes}
          pushDataLayer={pushDataLayer}
          deEmphasised={deEmphasised}
        />
        <div className="flex w-full flex-col gap-2 md:w-auto md:items-end">
          {vertical === 'CAR_INSURANCE' && pageType === 'page' && (
            <SectionCarInsurance
              countryCode={countryCode}
              carInsuranceSection={carInsuranceSection}
              productWithLivePrice={productWithLivePrice}
              sendAnalyticsForButtonApply={sendAnalyticsForButtonApply}
              staticText={staticText}
              ctaLinkOverride={ctaLinkOverride}
              carInsuranceQuote={carInsuranceQuote}
              statusCarInsuranceRequest={statusCarInsuranceRequest}
              defaultStaticCiTooltip={defaultStaticCiTooltip}
              defaultTemplateDynamicCiTooltip={defaultTemplateDynamicCiTooltip}
              product={product}
              CarInsuranceCtaComponent={carInsuranceCtaComponent}
            />
          )}

          {vertical === 'TRAVEL_INSURANCE' && !!travelQuotes && (
            <SectionTravelInsurance
              travelInsuranceQuotes={travelInsuranceQuotes}
              travelInsuranceCtaComponent={travelInsuranceCtaComponent}
            />
          )}

          {displayDefaultCtaButton && (
            <div className="flex items-center gap-1">
              <ApplyCtaLink
                product={product}
                ctaLinkOverride={ctaLinkOverride}
                loanParams={loanParams}
                target="_blank"
                rel="noreferrer noopener"
                className="block w-full md:inline md:w-auto"
              >
                <Button
                  endIcon={<MaterialIcon icon="open_in_new" size={2} />}
                  className="mhg-alpha-btn-small w-full md:w-general/2-col-width"
                  styleType="cta"
                  onClick={sendAnalyticsForButtonApply}
                >
                  {ctaLabelOverride ?? staticText.apply_now}
                </Button>
              </ApplyCtaLink>
            </div>
          )}
        </div>
      </div>
      <div className={classNames({ 'opacity-60': deEmphasised })}>
        <SectionAttributes
          componentAttributes={componentAttributes}
          product={product}
          index={index}
          url={url}
          calculateError={calculateError}
          highlightFirstAttribute={highlightFirstAttribute}
          pushDataLayer={pushDataLayer}
        />
      </div>
      <SectionApprovalOdds
        approvalOdds={approvalOdds}
        approvalOddsState={approvalOddsState}
        approvalOddsLabels={approvalOddsLabels}
        onApprovalOddsUnlockClick={sendAnalyticsForApprovalOddsUnlock}
        onApprovalOddsRefreshClick={sendAnalyticsForonApprovalOddsRefresh}
        staticText={staticText}
      />
      <ProductCardSectionsMapper
        loanParams={loanParams}
        productCardProps={props}
        plFilterConfig={plFilterConfig}
        carInsuranceCtaComponent={carInsuranceCtaComponent}
        priceLine={priceLine}
        productDetailsModalKeys={productDetailsModalKeys}
        getPdpModalConfigAction={getPdpModalConfigAction}
        isPreview={isPreview}
        renderPdpModalHero={renderPdpModalHero}
        travelInsuranceCtaComponent={travelInsuranceCtaComponent}
      />

      {vertical === 'CAR_INSURANCE' &&
        (carInsuranceSection?.urlPolicy || policyAttribute) && (
          <div className="mt-2 flex justify-end">
            <a
              target="_blank"
              rel="noreferrer noopener"
              href={
                carInsuranceSection?.urlPolicy
                  ? carInsuranceSection?.urlPolicy
                  : policyAttribute
              }
            >
              <Button
                variant="text"
                endIcon={<MaterialIcon icon="download" size={3} />}
              >
                {staticText.policy_document}
              </Button>
            </a>
          </div>
        )}
      {vertical === 'TRAVEL_INSURANCE' && urlPlanDetails && (
        <FeLink href={urlPlanDetails} target="_blank" rel="noopener noreferrer">
          <div className="mt-1">
            <SectionButton
              iconName="article"
              endIconName="open_in_new"
              iconClassName="overflow-visible size-[18px] md:size-[22px] md:mb-0 md:mr-2"
            >
              <span className="mhg-alpha-bullet-2 lg:mhg-alpha-bullet-1">
                {staticText.plan_details}
              </span>
            </SectionButton>
          </div>
        </FeLink>
      )}
      <EligibilityNote verdict={eligibility} className="mt-3" />
      {/* The card's last row, per the design. Only renders on a results page that compares plans;
          a credit card carries the pill at the top instead. */}
      <CompareToggle product={product} index={index} />
    </div>
  );
};
